import {
  App,
  Button,
  Form,
  Input,
  Modal,
  Popconfirm,
  Select,
  Space,
  Switch,
  Table,
  Tag,
  Tooltip,
  Typography,
} from 'antd';
import type { TableProps } from 'antd';
import { DeleteOutlined, EditOutlined, PlusOutlined } from '@ant-design/icons';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import {
  financeApi,
  type Account,
  type AccountInput,
  type AccountTreeNode,
  type AccountType,
} from '@/api/finance';
import { useAuthStore } from '@/store/auth';
import { PageHeader } from '@/shared/components/page-header';
import { extractError } from '@/shared/lib/error-message';

const TYPE_LABEL: Record<AccountType, string> = {
  ASSET: 'Asset',
  LIABILITY: 'Liability',
  INCOME: 'Income',
  EXPENSE: 'Expense',
  EQUITY: 'Equity',
};
const TYPE_COLOR: Record<AccountType, string> = {
  ASSET: 'success',
  LIABILITY: 'warning',
  INCOME: 'processing',
  EXPENSE: 'error',
  EQUITY: 'purple',
};

interface FlatNode extends Account {
  children?: FlatNode[];
}

function flatten(nodes: AccountTreeNode[]): FlatNode[] {
  return nodes.map((n) => ({
    ...n.account,
    children: n.children.length > 0 ? flatten(n.children) : undefined,
  }));
}

interface FormShape {
  code: string;
  name: string;
  accountType: AccountType;
  parentId?: number;
  postingAccount: boolean;
}

export function FinanceAccountsPage() {
  const { message } = App.useApp();
  const qc = useQueryClient();
  const canCreate = useAuthStore((s) => s.hasAuthority('FINANCE:CREATE'));
  const canEdit = useAuthStore((s) => s.hasAuthority('FINANCE:EDIT'));
  const canDelete = useAuthStore((s) => s.hasAuthority('FINANCE:DELETE'));

  const [editing, setEditing] = useState<Account | null>(null);
  const [creating, setCreating] = useState(false);
  const [form] = Form.useForm<FormShape>();

  const { data: tree, isLoading } = useQuery({
    queryKey: ['finance', 'accounts', 'tree'],
    queryFn: financeApi.accountTree,
  });

  const { data: flat } = useQuery({
    queryKey: ['finance', 'accounts'],
    queryFn: financeApi.listAccounts,
    staleTime: 60_000,
  });

  const upsert = useMutation({
    mutationFn: (values: FormShape) => {
      const body: AccountInput = {
        code: values.code.trim(),
        name: values.name.trim(),
        accountType: values.accountType,
        parentId: values.parentId ?? null,
        postingAccount: values.postingAccount,
      };
      return editing ? financeApi.updateAccount(editing.id, body) : financeApi.createAccount(body);
    },
    onSuccess: () => {
      message.success(editing ? 'Account updated' : 'Account created');
      qc.invalidateQueries({ queryKey: ['finance', 'accounts'] });
      setEditing(null);
      setCreating(false);
      form.resetFields();
    },
    onError: (e) => message.error(extractError(e)),
  });

  const toggle = useMutation({
    mutationFn: ({ id, value }: { id: number; value: boolean }) => financeApi.toggleAccount(id, value),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['finance', 'accounts'] }),
    onError: (e) => message.error(extractError(e)),
  });

  const remove = useMutation({
    mutationFn: (id: number) => financeApi.deleteAccount(id),
    onSuccess: () => {
      message.success('Account removed');
      qc.invalidateQueries({ queryKey: ['finance', 'accounts'] });
    },
    onError: (e) => message.error(extractError(e)),
  });

  const openCreate = () => {
    setEditing(null);
    setCreating(true);
    form.resetFields();
    form.setFieldsValue({ accountType: 'EXPENSE', postingAccount: true });
  };
  const openEdit = (row: Account) => {
    setCreating(false);
    setEditing(row);
    form.setFieldsValue({
      code: row.code,
      name: row.name,
      accountType: row.accountType,
      parentId: row.parentId ?? undefined,
      postingAccount: row.postingAccount,
    });
  };

  const open = creating || editing != null;
  const watchedType = Form.useWatch('accountType', form);
  const parentOptions = (flat ?? [])
    .filter((a) => a.accountType === watchedType && (!editing || a.id !== editing.id))
    .map((a) => ({ value: a.id, label: `${a.code} · ${a.name}` }));

  const data = tree ? flatten(tree) : [];

  const columns: TableProps<FlatNode>['columns'] = [
    {
      title: 'Code',
      dataIndex: 'code',
      width: 120,
      render: (c: string) => <Typography.Text code>{c}</Typography.Text>,
    },
    {
      title: 'Name',
      dataIndex: 'name',
      render: (n: string, row) => (
        <Space>
          <Typography.Text strong>{n}</Typography.Text>
          {row.system && <Tag>system</Tag>}
          {!row.active && <Tag>inactive</Tag>}
          {row.postingAccount && <Tag color="blue">posting</Tag>}
        </Space>
      ),
    },
    {
      title: 'Type',
      dataIndex: 'accountType',
      width: 130,
      render: (t: AccountType) => <Tag color={TYPE_COLOR[t]}>{TYPE_LABEL[t]}</Tag>,
    },
    {
      title: 'Active',
      dataIndex: 'active',
      width: 90,
      render: (active: boolean, row) => (
        <Tooltip title={row.system ? 'System accounts cannot be deactivated' : ''}>
          <Switch
            size="small"
            checked={active}
            disabled={!canEdit || row.system}
            onChange={(v) => toggle.mutate({ id: row.id, value: v })}
          />
        </Tooltip>
      ),
    },
    {
      title: 'Actions',
      width: 130,
      align: 'right',
      render: (_, row) => (
        <Space>
          {canEdit && (
            <Button size="small" type="text" icon={<EditOutlined />} onClick={() => openEdit(row)}>
              Edit
            </Button>
          )}
          {canDelete && !row.system && (
            <Popconfirm
              title="Delete account?"
              description="Only allowed if no ledger entries exist."
              okText="Delete"
              okButtonProps={{ danger: true }}
              onConfirm={() => remove.mutate(row.id)}
            >
              <Button size="small" danger type="text" icon={<DeleteOutlined />} />
            </Popconfirm>
          )}
        </Space>
      ),
    },
  ];

  return (
    <div style={{ display: 'grid', gap: 20 }}>
      <PageHeader
        eyebrow="Module / Finance"
        title="Chart of accounts"
        description="Hierarchical structure for postings. Only leaf (posting) accounts can receive ledger entries."
        count={flat ? { value: flat.length, label: 'accounts' } : undefined}
        actions={
          canCreate ? (
            <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>
              New account
            </Button>
          ) : null
        }
      />

      <Table
        rowKey="id"
        size="middle"
        loading={isLoading}
        columns={columns}
        dataSource={data}
        pagination={false}
        expandable={{ defaultExpandAllRows: true }}
      />

      <Modal
        title={editing ? `Edit ${editing.name}` : 'New account'}
        open={open}
        onCancel={() => {
          setEditing(null);
          setCreating(false);
        }}
        onOk={() => form.submit()}
        confirmLoading={upsert.isPending}
        okText={editing ? 'Save' : 'Create'}
        destroyOnHidden
      >
        {open && (
          <Form
            form={form}
            layout="vertical"
            onValuesChange={(changed) => {
              if (changed.accountType) form.setFieldValue('parentId', undefined);
            }}
            onFinish={(values) => upsert.mutate(values)}
          >
            <Form.Item label="Code" name="code" rules={[{ required: true, max: 20 }]}>
              <Input placeholder="e.g. 4100" />
            </Form.Item>
            <Form.Item label="Name" name="name" rules={[{ required: true, max: 200 }]}>
              <Input placeholder="e.g. Tuition Fee Income" />
            </Form.Item>
            <Form.Item label="Type" name="accountType" rules={[{ required: true }]}>
              <Select
                options={(Object.keys(TYPE_LABEL) as AccountType[]).map((t) => ({
                  value: t,
                  label: TYPE_LABEL[t],
                }))}
              />
            </Form.Item>
            <Form.Item
              label="Parent"
              name="parentId"
              tooltip="Optional. Parent must be of the same account type."
            >
              <Select allowClear options={parentOptions} placeholder="None (top-level)" />
            </Form.Item>
            <Form.Item
              label="Posting account"
              name="postingAccount"
              valuePropName="checked"
              tooltip="Only posting (leaf) accounts can receive ledger entries."
            >
              <Switch />
            </Form.Item>
          </Form>
        )}
      </Modal>
    </div>
  );
}
